import { Injectable } from '@angular/core';
import {
  DonationConfigInterface,
  FeaturesConfigInterface,
  MapConfigInterface,
  SessionConfigInterface,
  SessionTokenInterface,
  SiteConfigInterface,
} from '@models';
import { generalHelpers, UserInterface } from '@mzima-client/sdk';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly _currentSessionData$ = new BehaviorSubject<SessionTokenInterface>({
    accessToken: '',
    accessTokenExpires: 0,
    grantType: '',
    tokenType: '',
  });

  private readonly _currentUserData$ = new BehaviorSubject<UserInterface>({});
  private readonly _isLogged = new BehaviorSubject<boolean>(false);
  private readonly _clearedFilters = new BehaviorSubject<boolean>(false);
  private readonly _isFiltersVisible = new BehaviorSubject<boolean>(false);
  private readonly _isMainFiltersHidden = new BehaviorSubject<boolean>(true);
  private readonly _deploymentInfo = new BehaviorSubject<any>(false);
  private readonly _configLoaded = new BehaviorSubject<boolean>(false);
  private readonly _clearDateFilter = new Subject<void>();
  private readonly _isTimelineVisible = new BehaviorSubject<boolean>(true);

  readonly currentUserData$: Observable<UserInterface> = this._currentUserData$.asObservable();
  readonly isLogged$ = this._isLogged.asObservable();
  readonly isFiltersVisible$ = this._isFiltersVisible.asObservable();
  readonly isMainFiltersHidden$ = this._isMainFiltersHidden.asObservable();
  readonly deploymentInfo$ = this._deploymentInfo.asObservable();
  readonly configLoaded$ = this._configLoaded.asObservable();
  readonly clearedFilters$ = this._clearedFilters.asObservable();
  readonly clearDateFilter$ = this._clearDateFilter.asObservable();
  readonly isTimelineVisible$ = this._isTimelineVisible.asObservable();
  private clearedFilters = false;

  private currentSessionData: SessionTokenInterface = {
    accessToken: '',
    accessTokenExpires: 0,
    grantType: '',
    tokenType: '',
  };

  private currentUser: UserInterface = {
    userId: '',
    realname: '',
    email: '',
    role: '',
    permissions: [],
    gravatar: '',
    language: '',
    allowed_privileges: [],
    phone_number: '',
    zip_code: '',
    country_code: null,
  };

  private currentConfig: SessionConfigInterface = {
    features: {},
    site: {},
    map: {},
  };

  constructor() {
    this.loadSessionDataFromLocalStorage();
    this.loadUserDataFromLocalStorage();
    this.initSession();
    this._isLogged.next(!!this.currentUser.userId);
  }

  getCurrentUserData() {
    return this._currentUserData$.asObservable();
  }

  getLocalStorageNameMapper(key: string) {
    return `${generalHelpers.CONST.LOCAL_STORAGE_PREFIX}${key}`;
  }

  getSiteConfigurations(): SiteConfigInterface {
    return this.currentConfig.site;
  }

  setSiteDonationConfigurations(donation: DonationConfigInterface) {
    this.currentConfig.site.donation = donation;
  }

  getFeatureConfigurations() {
    return this.currentConfig.features;
  }

  getMapConfigurations() {
    return this.currentConfig.map;
  }

  getPrecision(locationPrecision: number) {
    return 1000 / Math.pow(10, locationPrecision);
  }

  setConfigurations(
    type: keyof SessionConfigInterface,
    data: FeaturesConfigInterface | SiteConfigInterface | MapConfigInterface,
  ) {
    this.currentConfig[type] = data as any;
    this._deploymentInfo.next({
      title: this.currentConfig['site']?.name ?? '',
      description: this.currentConfig['site']?.description ?? '',
      logo: this.currentConfig['site']?.image_header ?? '',
      private: this.currentConfig['site']?.private ?? false,
      email: this.currentConfig['site']?.email ?? '',
    });
    this.configLoaded = true;
  }

  loadSessionDataFromLocalStorage() {
    const expires = localStorage.getItem(this.getLocalStorageNameMapper('accessTokenExpires')) ?? 0;

    this.currentSessionData.accessToken =
      localStorage.getItem(this.getLocalStorageNameMapper('accessToken')) || '';
    this.currentSessionData.accessTokenExpires = +expires;
    this.currentSessionData.grantType =
      localStorage.getItem(this.getLocalStorageNameMapper('grantType')) || '';
    this.currentSessionData.tokenType =
      localStorage.getItem(this.getLocalStorageNameMapper('tokenType')) || '';

    const isFiltersVisible = localStorage.getItem(
      this.getLocalStorageNameMapper('isFiltersVisible'),
    );
    this._isFiltersVisible.next(isFiltersVisible ? JSON.parse(isFiltersVisible) : false);

    // const isMainFiltersHidden = localStorage.getItem(
    //   this.getLocalStorageNameMapper('main_filters_closed'),
    // );
    // this._isMainFiltersHidden.next(isMainFiltersHidden ? JSON.parse(isMainFiltersHidden) : false);

    const isTimelineVisible = localStorage.getItem(
      this.getLocalStorageNameMapper('isTimelineVisible'),
    );
    this._isTimelineVisible.next(isTimelineVisible ? JSON.parse(isTimelineVisible) : true);

    this._currentSessionData$.next(this.currentSessionData);
  }

  initSession() {
    // Mark new session when a new tab/window is opened (not reloaded)
    if (!sessionStorage.getItem('session_initialized')) {
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0); // Midnight today
      tomorrow.setDate(tomorrow.getDate() + 1); // Next day

      // Store session force expiry (next day, local time)
      localStorage.setItem(
        this.getLocalStorageNameMapper('session_expiry'),
        tomorrow.getTime().toString(),
      );
      sessionStorage.setItem('session_initialized', 'true');

      // Reset filters on new session
      this.clearFilters();
    } else {
      const now = Date.now();
      const expiry = parseInt(
        localStorage.getItem(this.getLocalStorageNameMapper('session_expiry')) || '0',
        10,
      );

      if (now > expiry) {
        // Expired — reset session marker
        sessionStorage.removeItem('session_initialized');
        localStorage.removeItem(this.getLocalStorageNameMapper('session_expiry'));

        // Call this function again to reinitialize session
        this.initSession();
      }
    }
  }

  get currentAuthToken() {
    return this.currentSessionData.accessToken;
  }

  get accessToSite(): boolean {
    return !this.currentConfig.site.private || !!this.currentAuthToken;
  }

  get siteFound(): boolean {
    return (
      this.currentConfig &&
      this.currentConfig.site &&
      Object.keys(this.currentConfig.site).length > 0
    );
  }

  set configLoaded(configLoaded: boolean) {
    this._configLoaded.next(configLoaded);
  }

  get currentAuthTokenType() {
    return this.currentSessionData.tokenType;
  }

  loadUserDataFromLocalStorage() {
    Object.entries(this.currentUser).map(([key]) => {
      this.currentUser[key as keyof UserInterface] =
        localStorage.getItem(this.getLocalStorageNameMapper(key)) ?? '';
    });
    this._currentUserData$.next(this.currentUser);
  }

  setCurrentUser(user: Partial<UserInterface>) {
    Object.entries(user).map(([key]) => {
      localStorage.setItem(
        this.getLocalStorageNameMapper(key),
        user[key as keyof UserInterface] as string,
      );
    });
    this.currentUser = Object.assign({}, this.currentUser, user);
    this._currentUserData$.next(this.currentUser);
    this._isLogged.next(!!this.currentUser.userId);
  }

  setSessionData(session: SessionTokenInterface) {
    Object.entries(session).map(([key]) => {
      localStorage.setItem(
        this.getLocalStorageNameMapper(key),
        session[key as keyof SessionTokenInterface] as string,
      );
    });
    this.currentSessionData = session;
    this._currentSessionData$.next(this.currentSessionData);
  }

  clearSessionData() {
    Object.keys(this.currentSessionData).forEach((key) => {
      localStorage.removeItem(this.getLocalStorageNameMapper(key));
    });
    this.currentSessionData = {
      accessToken: '',
      accessTokenExpires: 0,
      grantType: '',
      tokenType: '',
    };
    this._currentSessionData$.next(this.currentSessionData);
  }
  clearUserData() {
    const cookies = sessionStorage.getItem(this.getLocalStorageNameMapper('cookies'));
    Object.keys(this.currentUser).forEach((key) => {
      localStorage.removeItem(this.getLocalStorageNameMapper(key));
    });
    this.currentUser = {};
    if (cookies) sessionStorage.setItem(this.getLocalStorageNameMapper('cookies'), cookies);
    this._currentUserData$.next(this.currentUser);
    this.clearFilters();
  }

  clearFilters() {
    localStorage.removeItem(this.getLocalStorageNameMapper('activeSavedSearch'));
    localStorage.removeItem(this.getLocalStorageNameMapper('searchPostByKeyword'));
    localStorage.removeItem(this.getLocalStorageNameMapper('filters'));

    if (this.clearedFilters) return;
    this.clearedFilters = true;
    this._clearedFilters.next(true);
  }

  isFeatureEnabled(feature: keyof FeaturesConfigInterface) {
    return this.currentConfig.features[feature];
  }

  toggleFiltersVisibility(newValue: boolean) {
    localStorage.setItem(this.getLocalStorageNameMapper('isFiltersVisible'), newValue as any);
    this._isFiltersVisible.next(newValue);
  }

  toggleMainFiltersVisibility(newValue: boolean) {
    localStorage.setItem(this.getLocalStorageNameMapper('main_filters_closed'), newValue as any);
    this._isMainFiltersHidden.next(newValue);
  }

  emitClearDateFilter() {
    this._clearDateFilter.next();
  }

  toggleTimelineVisibility(newValue: boolean) {
    localStorage.setItem(this.getLocalStorageNameMapper('isTimelineVisible'), newValue as any);
    this._isTimelineVisible.next(newValue);
  }
}
